import { ACTION_ICONS, ROLE_VISIBILITY, isVisibleForRole } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import DContext from '../../context/DContext';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-multi-date-picker';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { useLoadingBar } from 'react-top-loading-bar';
import { formatSupplierInfo } from '../../helper/commonHelper';
import { inventoriesService } from '../../services/inventories.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { suppliersService } from '../../services/suppliers.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { formatDateToDisplay, formatDateYYYYMMDD } from "../../utils/dateUtils";
import { dropdownSelectStyles } from "../../utils/selectStyles";

const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base, borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", minHeight: 34, height: 34,
    fontSize: 13, boxShadow: "none", "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" }
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const toArray = (resData) => (Array.isArray(resData) ? resData : resData?.data || []);

const initialFilters = {
  dep_airport_code: "", arr_airport_code: "", departure_date: "", arrival_date: "",
  supplier: "", pnr: "", airline_code: "", flight_number: "", status: ""
};

const ListInventory = () => {
  const { start, complete } = useLoadingBar();
  const { authState} = useContext(DContext);
  const navigate = useNavigate();
  const show = ROLE_VISIBILITY.inventoryListPage;
   const supplierCode = authState?.userProfile?.supplier_code;     
  const showAddButton = isVisibleForRole(show.addButton, authState);
  const showFareEdit = isVisibleForRole(show.fareEditAction, authState);
  const showSeatColumn = isVisibleForRole(show.seatColumn, authState);
  const showEditDelete = isVisibleForRole(show.rowEditDeleteActions, authState);
  const columnCount = showSeatColumn ? 12 : 11;

  const [loading, setLoading] = useState(false);
  const [inventoryRes, setInventoryRes] = useState({
    pagination: {},
    inventories: []
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [airlineOptions, setAirlineOptions] = useState([]);
  // inventory whose fare slabs are shown in the View Fare popup
  const [fareInventory, setFareInventory] = useState(null);
  // Update Fare popup: "update" = update an existing fare slab, "add" = add a new fare slab
  const [fareTab, setFareTab] = useState("update");
  const [selectedSlabId, setSelectedSlabId] = useState("");
  const initialExistingFare = { basic: "", yq: "", yr: "", ot: "", infant: "", markup1: "", markup2: "" };
  const [existingFare, setExistingFare] = useState(initialExistingFare);
  const [fareSaving, setFareSaving] = useState(false);
  const initialNewFare = { seat: "", basic: "", yq: "", yr: "", ot: "", infant: "", markup1: "", markup2: "" };
  const [newFare, setNewFare] = useState(initialNewFare);

  const openUpdateFare = (inventory) => {
    setFareInventory(inventory);
    setFareTab("update");
    setSelectedSlabId("");
    setExistingFare(initialExistingFare);
    setNewFare(initialNewFare);
  }

  // Switching tabs in the Update Fare popup clears both tabs' fields
  const changeFareTab = (tab) => {
    setFareTab(tab);
    setSelectedSlabId("");
    setExistingFare(initialExistingFare);
    setNewFare(initialNewFare);
  }

  // Picking a fare slab binds all of its fares into the editable fields
  const handleSlabSelect = (slabId) => {
    setSelectedSlabId(slabId);
    const slab = (fareInventory?.fare_slabs || []).find((x) => String(x.inventory_pnr_id) === String(slabId));
    setExistingFare(slab ? {
      basic: slab.basic_amount ?? 0,
      yq: slab.yq_amount ?? 0,
      yr: slab.yr_amount ?? 0,
      ot: slab.ot_amount ?? 0,
      infant: slab.infant_amount ?? 0,
      markup1: slab.markup1 ?? 0,
      markup2: slab.markup2 ?? 0,
    } : initialExistingFare);
  }

  const handleExistingFareChange = (e) => {
    setExistingFare({ ...existingFare, [e.target.name]: e.target.value });
  }

  const existingGrandTotal = ["basic", "yq", "yr", "ot", "markup1"].reduce((sum, k) => sum + (Number(existingFare[k]) || 0), 0);
  const newGrandTotal = ["basic", "yq", "yr", "ot", "markup1"].reduce((sum, k) => sum + (Number(newFare[k]) || 0), 0);

  const handleUpdateExistingFare = async () => {
    const slab = (fareInventory?.fare_slabs || []).find((x) => String(x.inventory_pnr_id) === String(selectedSlabId));
    if (!slab) {
      toast.error("Please select a fare slab first");
      return;
    }
    const confirm = await withReactContent(Swal).fire({
      title: "Update fare?",
      text: `Are you sure you want to update this fare slab (total ₹ ${existingGrandTotal.toLocaleString("en-IN")})?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;
    try {
      setFareSaving(true);
      const res = await inventoriesService.updateExistingFare({
        inventory_pnr_id: Number(slab.inventory_pnr_id),
        seat_quantity: Number(slab.seat_quantity) || 0,
        basic_amount: Number(existingFare.basic) || 0,
        yq_amount: Number(existingFare.yq) || 0,
        yr_amount: Number(existingFare.yr) || 0,
        ot_amount: Number(existingFare.ot) || 0,
        total_amount: existingGrandTotal,
        infant_amount: Number(existingFare.infant) || 0,
        markup1: Number(existingFare.markup1) || 0,
        markup2: Number(existingFare.markup2) || 0,
      });
      if (res?.success) {
        toast.success(res.message);
        try { window.bootstrap?.Modal.getInstance(document.getElementById("UpdateFare"))?.hide(); } catch (err) { /* modal already closed */ }
        loadInventory();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setFareSaving(false);
    }
  }

  // Edit Inventory popup (UpdateInventoryPayload)
  const initialInventoryForm = { departure_time: "", arrival_time: "", departure_terminal: "", arrival_terminal: "", pnr: "", fare_code: "", hr: "" };
  const [inventoryForm, setInventoryForm] = useState(initialInventoryForm);
  const [inventorySaving, setInventorySaving] = useState(false);

  const trimTime = (t) => (t ? String(t).slice(0, 5) : "");

  const openEditInventory = (inventory) => {
    setFareInventory(inventory);
    setInventoryForm({
      departure_time: trimTime(inventory?.dep_time),
      arrival_time: trimTime(inventory?.arr_time),
      departure_terminal: inventory?.dep_terminal ?? inventory?.departure_terminal ?? "",
      arrival_terminal: inventory?.arr_terminal ?? inventory?.arrival_terminal ?? "",
      pnr: inventory?.pnr || "",
      fare_code: inventory?.fare_code ?? "",
      hr: inventory?.hr != null ? String(inventory.hr) : "",
    });
  }

  const handleInventoryFormChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === "departure_time" || name === "arrival_time") {
      // digits only, auto-insert the colon: 2355 -> 23:55
      const digits = value.replace(/\D/g, "").slice(0, 4);
      next = digits.length > 2 ? digits.slice(0, 2) + ":" + digits.slice(2) : digits;
    } else if (name === "hr") {
      next = value.replace(/\D/g, "").slice(0, 3);
    } else if (name === "pnr" || name === "fare_code") {
      next = value.toUpperCase();
    }
    setInventoryForm({ ...inventoryForm, [name]: next });
  }

  const handleInventoryStatusChange = async (inventory, checked) => {
    if (!showEditDelete) return;
    const newStatus = checked ? "active" : "inactive";
    const result = await withReactContent(Swal).fire({
      title: "Update status?",
      text: `Are you sure you want to mark inventory #${inventory?.inventory_id} (PNR ${inventory?.pnr || "-"}) as ${newStatus}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      //const res = await inventoriesService.update(inventory?.inventory_id, { status: newStatus });
      const pnrId = inventory?.fare_slabs?.[0]?.inventory_pnr_id;
      const res = await inventoriesService.updateInventoryDetail({
        inventory_pnr_id: Number(pnrId),
        status: newStatus
      });
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadInventory();
    }
  }

  const handleDeleteInventory = async (inventory) => {
    const result = await withReactContent(Swal).fire({
      title: "Delete inventory?",
      text: `Are you sure you want to delete inventory #${inventory?.inventory_id} (${inventory?.airline_code || ""} ${inventory?.flight_number || ""}, PNR ${inventory?.pnr || "-"})? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const res = await inventoriesService.delete(inventory?.inventory_id);
      if (res?.success) {
        toast.success(res.message);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      loadInventory();
    }
  }

  const handleUpdateInventory = async (e) => {
    e.preventDefault();
    const timeOk = (t) => /^([01]\d|2[0-3]):[0-5]\d$/.test(t);
    if (!timeOk(inventoryForm.departure_time)) return toast.error("Enter departure time as HH:MM");
    if (!timeOk(inventoryForm.arrival_time)) return toast.error("Enter arrival time as HH:MM");
    //if (!inventoryForm.pnr.trim()) return toast.error("PNR is required");
    // the PNR record being edited
    const pnrId = fareInventory?.fare_slabs?.[0]?.inventory_pnr_id;
    if (!pnrId) return toast.error("This inventory has no PNR record to update");
    const confirm = await withReactContent(Swal).fire({
      title: "Update inventory?",
      text: `Are you sure you want to update inventory #${fareInventory?.inventory_id}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;
    try {
      setInventorySaving(true);
      const res = await inventoriesService.updateInventoryDetail({
        inventory_pnr_id: Number(pnrId),
        departure_time: inventoryForm.departure_time,
        arrival_time: inventoryForm.arrival_time,
        departure_terminal: inventoryForm.departure_terminal.trim(),
        arrival_terminal: inventoryForm.arrival_terminal.trim(),
        pnr: inventoryForm.pnr.trim(),
        ...(inventoryForm.fare_code.trim() ? { fare_code: inventoryForm.fare_code.trim() } : {}),
        ...(inventoryForm.hr !== "" ? { hr: inventoryForm.hr } : {}),
      });
      if (res?.success) {
        toast.success(res.message);
        closeModal("UpdateInventory");
        loadInventory();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setInventorySaving(false);
    }
  }

  // Seat Details popup: "add" = add new seats with a fare, "decrease" = remove seats from an existing fare
  const [seatTab, setSeatTab] = useState("add");
  const initialAddSeat = { seats: "", basic: "", yq: "", yr: "", ot: "", infant: "", markup1: "", markup2: "" };
  const [addSeat, setAddSeat] = useState(initialAddSeat);
  const [decreaseSlabId, setDecreaseSlabId] = useState("");
  const [decreaseSeats, setDecreaseSeats] = useState("");
  const [seatSaving, setSeatSaving] = useState(false);

  const openSeatDetails = (inventory) => {
    setFareInventory(inventory);
    setSeatTab("add");
    setAddSeat(initialAddSeat);
    setDecreaseSlabId("");
    setDecreaseSeats("");
  }

  // Switching tabs in the Seat Details popup clears both tabs' fields
  const changeSeatTab = (tab) => {
    setSeatTab(tab);
    setAddSeat(initialAddSeat);
    setDecreaseSlabId("");
    setDecreaseSeats("");
  }

  const handleAddSeatChange = (e) => {
    setAddSeat({ ...addSeat, [e.target.name]: e.target.value });
  }

  const addSeatGrandTotal = ["basic", "yq", "yr", "ot", "markup1"].reduce((sum, k) => sum + (Number(addSeat[k]) || 0), 0);
  const decreaseSlab = (fareInventory?.fare_slabs || []).find((x) => String(x.inventory_pnr_id) === String(decreaseSlabId));
  const decreaseAvailable = Number(decreaseSlab?.available_seats) || 0;

  const closeModal = (id) => {
    try { window.bootstrap?.Modal.getInstance(document.getElementById(id))?.hide(); } catch (err) { /* modal already closed */ }
  }

  // POST addNewSeat with AddNewSeatPayload; form = { seats, basic, yq, yr, ot, infant, markup1, markup2 }
  const submitNewSeat = async (form, modalId) => {
    const seats = Number(form.seats);
    if (!(seats > 0)) {
      toast.error("Enter the number of seats to add");
      return;
    }
    // the new seats are added to this inventory's PNR record
    const pnrId = fareInventory?.fare_slabs?.[0]?.inventory_pnr_id;
    if (!pnrId) {
      toast.error("This inventory has no PNR record to add seats to");
      return;
    }
    const total = ["basic", "yq", "yr", "ot", "markup1"].reduce((sum, k) => sum + (Number(form[k]) || 0), 0);
    const confirm = await withReactContent(Swal).fire({
      title: "Add seats?",
      text: `Are you sure you want to add ${seats} seat${seats == 1 ? "" : "s"} at ₹ ${total.toLocaleString("en-IN")}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;
    try {
      setSeatSaving(true);
      const res = await inventoriesService.addNewSeat({
        inventory_pnr_id: Number(pnrId),
        action: "add",
        seat_quantity: seats,
        basic_amount: Number(form.basic) || 0,
        yq_amount: Number(form.yq) || 0,
        yr_amount: Number(form.yr) || 0,
        ot_amount: Number(form.ot) || 0,
        total_amount: total,
        infant_amount: Number(form.infant) || 0,
        markup1: Number(form.markup1) || 0,
        markup2: Number(form.markup2) || 0,
      });
      if (res?.success) {
        toast.success(res.message);
        closeModal(modalId);
        loadInventory();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setSeatSaving(false);
    }
  }

  const handleAddNewSeat = () => submitNewSeat(addSeat, "AddUpdateSeat");
  // Update Fare popup > Add New Fare tab (its "Seat" field is the number of seats)
  const handleAddNewFare = () => submitNewSeat({ ...newFare, seats: newFare.seat }, "UpdateFare");

  const handleDecreaseSeat = async () => {
    const seats = Number(decreaseSeats);
    if (!decreaseSlab || !(seats > 0)) return;
    const confirm = await withReactContent(Swal).fire({
      title: "Decrease seats?",
      text: `Are you sure you want to remove ${seats} seat${seats == 1 ? "" : "s"} from the ₹ ${Number(decreaseSlab.total_amount ?? 0).toLocaleString("en-IN")} fare?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d93025",
      confirmButtonText: "Yes, decrease",
      cancelButtonText: "Cancel"
    });
    if (!confirm.isConfirmed) return;
    try {
      setSeatSaving(true);
      const res = await inventoriesService.decreaseExistingSeat({
        inventory_pnr_id: Number(decreaseSlab.inventory_pnr_id),
        action: "remove",
        seat_quantity: seats,
      });
      if (res?.success) {
        toast.success(res.message);
        closeModal("AddUpdateSeat");
        loadInventory();
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setSeatSaving(false);
    }
  }

  const handleNewFareChange = (e) => {
    setNewFare({ ...newFare, [e.target.name]: e.target.value });
  }

  useEffect(() => {
    loadInventory();
  }, [pagingLimit, pageValue, appliedFilters, supplierCode])

  useEffect(() => {
    console.log(authState);
    
    loadAirlines();
  }, [])

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadInventory = async () => {
    try {
      setLoading(true);
      start();
  
      const res = await inventoriesService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
        // my inventory: always limited to the logged-in supplier
        ...(supplierCode ? { supplier_code: supplierCode } : {}),
      });
      if (res?.success) {
        setInventoryRes({
          pagination: res.resData?.pagination || {},
          inventories: res.resData?.data || []
        })
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
      complete();
    }
  }

  // Airline dropdown: airlines > list (status = active); value is the airline code the inventory API filters on
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlineOptions(toArray(res.resData).map(a => ({ value: a.airline_code, label: `${a.airline_name} (${a.airline_code})` })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Departure / Arrival airport dropdowns: airports > list (status = active), searched on the server
  const loadAirports = async (search = "") => {
    try {
      const searchText = search.trim();
       // Skip API call for 1 or 2 characters
      if (searchText.length > 0 && searchText.length < 3) {
        return [];
      }

      //const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...(search ? { search } : {}) });
      const params = {
        page: 1,
        limit: 20,
        is_popular: true,
        status: "active",
        ...(searchText.length === 3
          ? { iata_id: searchText }
          : searchText.length > 3
            ? { search: searchText }
            : {}),
      };
      const res = await airportsService.list(params);

      if (res?.success) {
        return toArray(res.resData).map(a => ({
          value: a.airport_code,
          label: `${a.airport_name} (${a.airport_code}), ${a.city_name}`
        }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  // Supplier dropdown: suppliers > list (status = active), searched on the server; value is the agency name the inventory API filters on
  const loadSuppliers = async (search = "") => {
    try {
      const res = await suppliersService.list({ page: 1, status: "active", ...(search ? { search } : {}) });
      if (res?.success) {
        return (res.resData?.data || []).map(s => ({ value: s.agency_name, label: s.agency_name }));
      }
      toast.error(res?.message);
      return [];
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  // const handleApply = (e) => {
  //   e?.preventDefault();
  //   setPageValue(1);
  //   setAppliedFilters({ ...filters });
  // }
  const handleApply = (e) => {
  e?.preventDefault();

  setPageValue(1);

  setAppliedFilters({
    ...filters,
    departure_date: filters.departure_date
      ? formatDateYYYYMMDD(filters.departure_date)
      : "",
  });
};

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  }

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  }

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  }

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  }

  // No update/delete endpoints exist for inventory yet
  const handleActionNotAvailable = () => {
    toast("This action is not available yet");
  }

  const inventories = inventoryRes?.inventories || [];
  const totalRecords = inventoryRes?.pagination?.total || 0;
  const totalPage = Math.max(1, inventoryRes?.pagination?.totalPages || 1);
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter(n => n >= 1 && n <= totalPage).sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  }

  const pageBtn = (active, disabled) => ({
    minWidth: 32, height: 32, padding: "0 8px", borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer"
  });

  const csvHeaders = [
    { label: "Inventory ID", key: "inventory_id" },
    { label: "Supplier", key: "supplier_info" },
    { label: "Departure", key: "dep_airport_code" },
    { label: "Arrival", key: "arr_airport_code" },
    { label: "Departure Date", key: "dep_date" },
    { label: "Departure Time", key: "dep_time" },
    { label: "Arrival Time", key: "arr_time" },
    { label: "Airline", key: "airline_name" },
    { label: "Flight No", key: "flight_number" },
    { label: "PNR", key: "pnr" },
    { label: "Seats", key: "seat_quantity" },
  ];

  // Row highlight: no PNR = orange, inactive = light red
  const rowBackground = (inventory) => {
    if (!inventory?.pnr) return "#ffe8cc";
    if (inventory?.inventory_pnr_status === "inactive") return "#fdecec";
    return "";
  }

  const filterInput = { borderRadius: 8, border: "1px solid #e8edf5", background: "#f8f9fe", fontSize: 13, height: 34 };

  // Inventory summary shown at the top of the Fare / Update Fare / Seat Details popups
  const inventoryInfo = fareInventory && (
    <div className="row g-3 mb-3">
      {[
        { label: "Supplier", value: fareInventory.supplier_name || formatSupplierInfo(fareInventory.supplier_info) },
        { label: "Departure", value: `${fareInventory.dep_city_name || "-"} (${fareInventory.dep_airport_code || "-"})` },
        { label: "Arrival", value: `${fareInventory.arr_city_name || "-"} (${fareInventory.arr_airport_code || "-"})` },
        { label: "Dept – Arr Time", value: `${String(fareInventory.dep_time || "-").slice(0, 5)} – ${String(fareInventory.arr_time || "-").slice(0, 5)}` },
        { label: "Airline | Flight No", value: `${fareInventory.airline_name || fareInventory.airline_code || "-"} | ${fareInventory.airline_code || "-"} | ${fareInventory.flight_number || "-"}` },
        { label: "Dept. Date", value: formatDateToDisplay(fareInventory.dep_date) || "-" },
      ].map((f) => (
        <div className="col-6 col-md-4" key={f.label}>
          <div style={{ fontSize: "11px", fontWeight: "700", color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "2px" }}>{f.label}</div>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#1f2a44" }}>{f.value}</div>
        </div>
      ))}
    </div>
  );  

  return (
    <>
      <section className="section dashboard">

        {/* ── Page header ── */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Inventory</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Inventory</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>Inventory List</span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            {showAddButton && (
              <Link to="/inventory/add" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                <i className="bi bi-plus-lg"></i> Add Inventory
              </Link>
            )}
            <CSVLink
              data={inventories.map(i => ({ ...i, supplier_info: formatSupplierInfo(i.supplier_info) }))}
              headers={csvHeaders}
              filename="inventory.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        {/* ── Filter Card ── */}
        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, dep_airport_code: o ? o.value : "" })}
                  placeholder="Departure Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />
              </div>
              <div className="col-12 col-md-3">
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirports}
                  onChange={(o) => setFilters({ ...filters, arr_airport_code: o ? o.value : "" })}
                  placeholder="Arrival Airport"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.departure_date || null}                 
                  onChange={(d) => setFilters({ ...filters, departure_date: d ? d.format("DD-MM-YYYY") : "" })}
                  format="DD-MM-YYYY"
                  placeholder="Departure date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-3">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.arrival_date || null}
                  onChange={(d) => setFilters({ ...filters, arrival_date: d ? d.format("YYYY-MM-DD") : "" })}
                  format="YYYY-MM-DD"
                  placeholder="Arrival date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="pnr" placeholder="PNR" value={filters.pnr} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={airlineOptions}
                  value={airlineOptions.find(o => o.value === filters.airline_code) || null}
                  onChange={(o) => setFilters({ ...filters, airline_code: o ? o.value : "" })}
                  placeholder="Select Airline"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />
              </div>
              <div className="col-6 col-md-2">
                <input type="text" className="form-control" name="flight_number" placeholder="Flight No." value={filters.flight_number} onChange={handleFilterChange} style={filterInput} />
              </div>
              <div className="col-6 col-md-2">
                <Select
                  options={STATUS_FILTER_OPTIONS}
                  value={STATUS_FILTER_OPTIONS.find(o => o.value === filters.status) || null}
                  onChange={(o) => setFilters({ ...filters, status: o ? o.value : "" })}
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={dropdownSelectStyles}
                />
              </div>
              <div className="col-6 col-md-2 d-flex gap-2">
                <button type="submit" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
                  <i className="bi bi-search"></i> Apply
                </button>
                <button type="button" onClick={handleReset} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" }}>
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* ── Table Card ── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div>

                {/* Card Header */}
                {/* <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{
                      width: "34px", height: "34px", borderRadius: "9px",
                      background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <i className="bi bi-airplane-fill text-white" style={{ fontSize: "14px" }}></i>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ color: "var(--btn-hover-bg)", fontSize: "14px" }}>Inventory List</h6>
                      <span style={{ fontSize: "11px", color: "#475569" }}>
                        Total: <strong style={{ color: "var(--btn-bg)" }}>{inventoryRes?.pagination?.total ?? 0}</strong> records
                      </span>
                    </div>
                  </div>
                  <Link to="/inventory/add" style={{ textDecoration: "none" }}>
                    <button className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white"
                      style={{
                        background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))",
                        borderRadius: "8px", border: "none",
                        boxShadow: "0 4px 12px rgba(14,106,150,0.25)",
                        fontSize: "13px", height: "34px", padding: "0 16px"
                      }}>
                      <i className="bi bi-plus-circle-fill"></i> Add New PNR
                    </button>
                  </Link>
                </div> */}

                {/* Loading Overlay */}
                {loading && (
                  <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    background: "rgba(255,255,255,0.7)",
                    display: "flex", justifyContent: "center", alignItems: "center",
                    zIndex: 10, borderRadius: "14px"
                  }}>
                    <ContentLoader loaderType="circleSpin" />
                  </div>
                )}

                {/* Table */}
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr style={{ background: "#f4f6fb" }}>
                        {/* {["Supplier", "Departure", "Arrival", "Dept – Arr Time", "Airline (Flight No)", "PNR", "Dept. Date", "Seats", "Fare", ...(showSeatColumn ? ["+/- Seat"] : []), "Status", "Action"].map((h, i) => ( */}
                        {["S.No.", "Airline (Flight No)", "Departure", "Arrival", "Dept – Arr Time", "PNR", "Dept. Date", "Seats", "Fare", ...(showSeatColumn ? ["+/- Seat"] : []), "Status", "Action"].map((h, i) => (
                          <th key={i} style={{
                            fontSize: "11px", fontWeight: "700", color: "#475569",
                            textTransform: "uppercase", letterSpacing: "0.6px",
                            border: "none", padding: "10px 12px", whiteSpace: "nowrap",
                            background: "#f4f6fb"
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inventories.length > 0 ? (
                        inventories.map((inventory, idx) => (
                          <tr
                            key={`${inventory?.inventory_id}-${inventory?.pnr}-${idx}`}
                            style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle", background: rowBackground(inventory) }}
                            onMouseEnter={(e) => { if (!rowBackground(inventory)) e.currentTarget.style.background = "#f8f9fe"; }}
                            onMouseLeave={(e) => (e.currentTarget.style.background = rowBackground(inventory))}
                          >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + idx + 1}</td>
                            {/* Supplier */}
                            {/* <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-start gap-2">
                                <div
                                  style={{
                                    width: "26px",
                                    height: "26px",
                                    borderRadius: "7px",
                                    background: "#e6f1f8",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                  }}
                                >
                                  <i className="bi bi-building" style={{ fontSize: "11px", color: "var(--btn-bg)" }}></i>
                                </div>
                                <div>
                                  <div style={{ fontWeight: "700", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                    {inventory?.supplier_name}
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {inventory?.supplier_code}
                                  </div>
                                </div>
                              </div>
                            </td> */}

                            {/* Airline (Flight No) */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <img
                                  style={{
                                    width: "22px",
                                    height: "22px",
                                    borderRadius: "5px",
                                    objectFit: "contain",
                                    background: "#f4f6fb",
                                    padding: "2px"
                                  }}
                                  src={`https://images.kiwi.com/airlines/32/${inventory?.airline_code}.png`}
                                  alt={inventory?.airline_code || "airline"}
                                />
                                <div>
                                  <div style={{ fontWeight: "700", color: "#374151", fontSize: "12px", lineHeight: 1.1 }}>
                                    {inventory?.airline_name || "-"} 
                                  </div>
                                  <div style={{ fontSize: "11px", color: "#6b7280" }}>
                                    {inventory?.airline_code || "-"} - {inventory?.flight_number || "-"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Departure */}
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ fontWeight: "600", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                {inventory?.dep_city_name || "-"}
                              </div>
                              <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  {inventory?.dep_airport_code || "-"}
                                  </div>
                              {/* <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#e6f1f8",
                                  color: "var(--btn-bg)",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.dep_airport_code || "-"}
                              </span> */}
                            </td>

                            {/* Arrival */}
                            <td style={{ padding: "10px 12px" }}>
                              <div style={{ fontWeight: "600", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                                {inventory?.arr_city_name || "-"}
                              </div>
                               <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  {inventory?.arr_airport_code || "-"}
                                  </div>
                              {/* <span
                                style={{
                                  marginLeft: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  background: "#e6f1f8",
                                  color: "var(--btn-bg)",
                                  borderRadius: "5px",
                                  padding: "1px 6px"
                                }}
                              >
                                {inventory?.arr_airport_code || "-"}
                              </span> */}
                            </td>

                            {/* Dept – Arr Time */}
                           <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                <i className="bi bi-clock me-1" style={{ color: "#475569", fontSize: "10px" }}></i>
                                {inventory?.dep_time || "-"}
                              </span>
                              <span style={{ margin: "0 4px", color: "#475569" }}>→</span>
                              <span style={{ fontSize: "12px", color: "#374151", fontWeight: "600" }}>
                                {inventory?.arr_time || "-"}
                              </span>
                            </td>
                            

                            {/* PNR */}
                            <td style={{ padding: "10px 12px" }}>
                              <span
                                style={{                                  
                                  color: "var(--btn-hover-bg)",
                                  fontSize: "11px",
                                  fontWeight: "700",
                                }}
                              >
                                {inventory?.pnr || "-"}
                              </span>
                            </td>

                            {/* Dept. Date */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <span
                                style={{
                                  fontSize: "11px",
                                  color: "#6b7280",
                                  fontWeight: "600",
                                  background: "#f4f6fb",
                                  borderRadius: "6px",
                                  padding: "2px 8px"
                                }}
                              >
                                <i className="bi bi-calendar3 me-1" style={{ color: "#475569" }}></i>
                                {formatDateToDisplay(inventory?.dep_date) || "-"}
                              </span>
                            </td>

                            {/* Seats */}
                            <td style={{ padding: "10px 12px" }}>
                              {/* <span
                                style={{
                                  background: "#ecfdf5",
                                  color: "#065f46",
                                  borderRadius: "6px",
                                  padding: "3px 8px",
                                  fontSize: "11px",
                                  fontWeight: "700"
                                }}
                              >
                                <i className="bi bi-person-fill me-1" style={{ fontSize: "10px" }}></i>
                                {inventory?.seat_quantity ?? 0}
                              </span> */}
                              <div style={{ fontWeight: "600", color: "var(--btn-hover-bg)", fontSize: "12px" }}>
                              A:  {inventory?.available_seats ?? 0}
                              </div>
                               <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  B:  {inventory?.booked_quantity ?? 0}
                                  </div>
                                   <div style={{ fontSize: "10px", color: "#6b7280" }}>
                                  T:  {inventory?.seat_quantity ?? 0}
                                  </div>
                            </td>

                            {/* Fare */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                style={{
                                  background: "#e6f1f8",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0,
                                  marginRight: "4px"
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#ViewFare"
                                onClick={() => setFareInventory(inventory)}
                                title="View Fare"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#dde3ef")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#e6f1f8")}
                              >
                                <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: "11px", color: "var(--btn-bg)" }}></i>
                              </button>
                              {showFareEdit && <button
                                type="button"
                                style={{
                                  background: "#fffbeb",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#UpdateFare"
                                onClick={() => openUpdateFare(inventory)}
                                title="Update Fare"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#fef3c7")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#fffbeb")}
                              >
                                <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: "11px", color: "#b45309" }}></i>
                              </button>}
                            </td>

                            {/* +/- Seat */}
                            {showSeatColumn && <td style={{ padding: "10px 12px" }}>
                              <button
                                type="button"
                                style={{
                                  background: "#ecfdf5",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                data-bs-toggle="modal"
                                data-bs-target="#AddUpdateSeat"
                                onClick={() => openSeatDetails(inventory)}
                                title="Add / Remove Seats"
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#d1fae5")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#ecfdf5")}
                              >
                                <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: "11px", color: "#065f46" }}></i>
                              </button>
                            </td>}

                            {/* Active / Inactive */}
                            <td style={{ padding: "10px 12px" }}>
                              <div className="form-check form-switch m-0 d-flex align-items-center justify-content-center p-0">
                                <input
                                  type="checkbox"
                                  role="switch"
                                  className="form-check-input m-0"
                                  checked={inventory?.inventory_pnr_status !== "inactive"}
                                  onChange={(e) => handleInventoryStatusChange(inventory, e.target.checked)}
                                  disabled={!showEditDelete}
                                  title={inventory?.inventory_pnr_status === "inactive" ? "Inactive" : "Active"}
                                  style={{
                                    width: "38px",
                                    height: "20px",
                                    cursor: "pointer",
                                    backgroundColor: inventory?.inventory_pnr_status === "inactive" ? "#dc2626" : "#16a34a",
                                    borderColor: inventory?.inventory_pnr_status === "inactive" ? "#dc2626" : "#16a34a",
                                    backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e\")",
                                    backgroundPosition: inventory?.inventory_pnr_status === "inactive" ? "left center" : "right center",
                                    opacity: 1
                                  }}
                                />
                              </div>
                            </td>

                            {/* Action */}
                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                              <button
                                type="button"
                                className="btn btn-sm me-1"
                                style={{ background: "#e6f1f8", color: "var(--btn-bg)", border: "none", borderRadius: "7px", width: "28px", height: "28px", padding: 0 }}
                                onClick={() => navigate("/inventory/details/" + inventory?.inventory_id, { state: { inventory, from: "/inventory/list" } })}
                                title="View"
                              >
                                <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: "11px" }}></i>
                              </button>
                              {showEditDelete && <>
                              <button
                                className="btn btn-sm me-1"
                                style={{
                                  background: "#e6f1f8",
                                  color: "var(--btn-bg)",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Edit"
                                data-bs-toggle="modal"
                                data-bs-target="#UpdateInventory"
                                onClick={() => openEditInventory(inventory)}
                              >
                                <i className={`bi ${ACTION_ICONS.edit}`} data-action="edit" style={{ fontSize: "11px" }}></i>
                              </button>

                              {/* <button
                                className="btn btn-sm me-1"
                                style={{
                                  background: "#ecfdf5",
                                  color: "#065f46",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Refresh"
                                onClick={loadInventory}
                              >
                                <i className="bi bi-arrow-clockwise" style={{ fontSize: "11px" }}></i>
                              </button> */}

                              <button
                                className="btn btn-sm"
                                style={{
                                  background: "#fff1f1",
                                  color: "#dc2626",
                                  border: "none",
                                  borderRadius: "7px",
                                  width: "28px",
                                  height: "28px",
                                  padding: 0
                                }}
                                title="Delete"
                                onClick={() => handleDeleteInventory(inventory)}
                              >
                                <i className={`bi ${ACTION_ICONS.delete}`} data-action="delete" style={{ fontSize: "11px" }}></i>
                              </button>
                              </>}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={columnCount} className="text-center py-5">
                            <div style={{ color: "#475569" }}>
                              <i
                                className="bi bi-inbox"
                                style={{ fontSize: "36px", display: "block", marginBottom: "8px" }}
                              ></i>
                              <span style={{ fontSize: "13px" }}>No inventory found</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-3" style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}>
                  <div>Showing <b>{fromRecord}–{toRecord}</b> of <b>{totalRecords}</b> inventories</div>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span>Rows</span>
                    <select className="form-select form-select-sm" style={{ width: 100, borderRadius: 8 }} value={pagingLimit} onChange={handlePagingLimit}>
                      {["10", "20", "50", "100", "500"].map(v => <option key={v} value={v}>{v}/page</option>)}
                    </select>
                    <span>Go</span>
                    <input type="number" min="1" max={totalPage} className="form-control form-control-sm text-center" style={{ width: 60, borderRadius: 8 }}
                      value={goPage} onChange={(e) => setGoPage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleGoPage()} placeholder={String(pageValue)} />
                    <span>/ {totalPage}</span>
                    <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>Go</button>
                    <div className="d-flex align-items-center gap-1">
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(1)}><i className="bi bi-chevron-double-left"></i></button>
                      <button type="button" disabled={pageValue <= 1} style={pageBtn(false, pageValue <= 1)} onClick={() => handlePagination(pageValue - 1)}><i className="bi bi-chevron-left"></i></button>
                      {getPageNumbers().map((n, i) => n === "..."
                        ? <span key={"e" + i} className="px-1">...</span>
                        : <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => handlePagination(n)}>{n}</button>)}
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(pageValue + 1)}><i className="bi bi-chevron-right"></i></button>
                      <button type="button" disabled={pageValue >= totalPage} style={pageBtn(false, pageValue >= totalPage)} onClick={() => handlePagination(totalPage)}><i className="bi bi-chevron-double-right"></i></button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
      </section>

      {/* ── View Fare Modal ── */}
      <div className="modal fade" id="ViewFare" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))" }}></div>
            <div className="modal-header border-0">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-eye-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: "15px" }}>Fare Details</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#fff" }}>View current fare slabs</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              {inventoryInfo}
              <table className="table mb-0" style={{ fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f4f6fb" }}>
                    {["Fare", "Sold Seat", "Unsold Seat", "Total Seat", "Departure Date"].map((h, i) => (
                      <th key={i} style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px", border: "none", padding: "10px 12px", background: "#f4f6fb" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(fareInventory?.fare_slabs || []).map((slab, i) => (
                    <tr key={slab?.inventory_pnr_id ?? i} style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#ecfdf5", color: "#065f46", borderRadius: "6px", padding: "3px 8px", fontSize: "12px", fontWeight: "700" }}>₹ {Number(slab?.total_amount ?? 0).toLocaleString("en-IN")}</span>
                      </td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>{slab?.booked_quantity ?? 0}</td>
                      <td style={{ padding: "10px 12px", color: "#374151", fontWeight: "600" }}>{slab?.available_seats ?? 0}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ background: "#e6f1f8", color: "var(--btn-bg)", borderRadius: "6px", padding: "2px 8px", fontSize: "11px", fontWeight: "700" }}>{slab?.seat_quantity ?? 0}</span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: "600", background: "#f4f6fb", borderRadius: "6px", padding: "2px 8px" }}>
                          <i className="bi bi-calendar3 me-1" style={{ color: "#475569" }}></i>{fareInventory?.dep_date || "-"}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(fareInventory?.fare_slabs || []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4" style={{ fontSize: "13px" }}>No fare slabs found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Update Fare Modal ── */}
      <div className="modal fade" id="UpdateFare" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))" }}></div>
            <div className="modal-header border-0">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-pencil-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: "15px" }}>Update Fare</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#fff" }}>Modify fare details for this PNR</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              {inventoryInfo}

              {/* PNR + Warning */}
              {/* <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>PNR Number</label>
                  <div className="input-group input-group-sm">
                    <span className="input-group-text" style={{ background: "#e6f1f8", border: "1.5px solid #dde3ef", borderRight: "none", borderRadius: "8px 0 0 8px" }}>
                      <i className="bi bi-upc" style={{ color: "var(--btn-bg)", fontSize: "12px" }}></i>
                    </span>
                    <input type="text" className="form-control form-control-sm" id="txtUpdate_PnrNumber" value={fareInventory?.pnr || ""} readOnly
                      style={{ border: "1.5px solid #dde3ef", borderLeft: "none", borderRadius: "0 8px 8px 0", background: "#f8f9fe", fontSize: "13px", fontWeight: "700" }} />
                  </div>
                </div>
                <div className="col-md-8 d-flex align-items-end">
                  <div className="d-flex align-items-center gap-2 p-2 rounded-3 w-100" style={{ background: "#fff1f1", border: "1.5px solid #fca5a5" }}>
                    <i className="bi bi-exclamation-circle-fill" style={{ color: "#dc2626", fontSize: "13px", flexShrink: 0 }}></i>
                    <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: "500" }}>Please select fare slab first, before new update!</span>
                  </div>
                </div>
              </div> */}

              {/* Tabs */}
              <ul className="nav nav-tabs mb-3" role="tablist" style={{ borderBottom: "1px solid #eef0f8" }}>
                {[
                  { key: "update", label: "Update Existing Fare", icon: ACTION_ICONS.edit },
                  { key: "add", label: "Add New Fare", icon: "bi-plus-circle-fill" },
                ].map((t) => (
                  <li className="nav-item" key={t.key} role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={fareTab === t.key}
                      className={`nav-link d-flex align-items-center gap-2 ${fareTab === t.key ? "active" : ""}`}
                      onClick={() => changeFareTab(t.key)}
                      style={{ fontSize: "13px", fontWeight: "600", color: fareTab === t.key ? "var(--btn-bg)" : "#6b7a90" }}
                    >
                      <i className={`bi ${t.icon}`} style={{ fontSize: "12px" }}></i> {t.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Existing Info (update tab only) */}
              {fareTab === "update" && (
                <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                  {/* <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-info-circle-fill" style={{ color: "var(--btn-bg)", fontSize: "13px" }}></i>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--btn-bg)", textTransform: "uppercase", letterSpacing: "0.7px" }}>Existing Info</span>
                  </div> */}
                  <div className="row g-2 align-items-end">
                    <div className="col-md-4">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Fare Slab</label>
                      <select className="form-select form-select-sm" id="txtUpdate_FareSlab" value={selectedSlabId}
                        onChange={(e) => handleSlabSelect(e.target.value)}
                        style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                        <option value="">Select</option>
                        {(fareInventory?.fare_slabs || []).map((x) => (
                          <option key={x.inventory_pnr_id} value={x.inventory_pnr_id}>₹ {Number(x.total_amount ?? 0).toLocaleString("en-IN")}</option>
                        ))}
                      </select>
                    </div>
                    {[
                      { id: "txtUpdate_BasicFare", name: "basic", label: "Basic Fare" },
                      { id: "txtUpdate_YQFare", name: "yq", label: "YQ Fare" },
                      { id: "txtUpdate_YRFare", name: "yr", label: "YR Fare" },
                      { id: "txtUpdate_OTFare", name: "ot", label: "OT Fare" },
                      { id: "txtUpdate_InfantFare", name: "infant", label: "Infant Fare" },
                    ].map(({ id, name, label }) => (
                      <div className="col-md-4" key={id}>
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                        <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                          disabled={!selectedSlabId} value={existingFare[name]} onChange={handleExistingFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    ))}
                    <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Markup</label>
                        <input type="number" min="0" className="form-control form-control-sm" id="txtUpdate_Markup1" name="markup1" placeholder="0"
                         disabled={!selectedSlabId} value={existingFare.markup1} onChange={handleExistingFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    <div className="col-md-4">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Grand Total</label>
                      <input type="text" className="form-control form-control-sm" id="txtUpdate_GrandTotalFare" disabled
                        value={selectedSlabId ? existingGrandTotal : "0"} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "#f8f9fe", fontSize: "13px" }} />
                    </div>
                    {/* {[
                      { id: "txtUpdate_Markup1", name: "markup1", label: "Markup 1" },
                      { id: "txtUpdate_Markup2", name: "markup2", label: "Markup 2" },
                    ].map(({ id, name, label }) => (
                      <div className="col-md-4" key={id}>
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                        <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                          disabled={!selectedSlabId} value={existingFare[name]} onChange={handleExistingFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    ))} */}
                    <div className="col-md-4 d-flex align-items-end">
                      <button type="button" onClick={handleUpdateExistingFare} disabled={!selectedSlabId || fareSaving}
                        className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                        style={{ background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(14,106,150,0.25)", fontSize: "13px", height: "34px" }}>
                        <i className="bi bi-floppy-fill"></i> {fareSaving ? "Updating..." : "Update Fare"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* New fare values (add tab only) */}
              {fareTab === "add" && <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                {/* <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-plus-circle-fill" style={{ color: "var(--btn-bg)", fontSize: "13px" }}></i>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--btn-bg)", textTransform: "uppercase", letterSpacing: "0.7px" }}>Fare Details</span>
                </div> */}
                <div className="row g-2 align-items-end">
                  <div className="col-md-2">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Seat</label>
                    <input type="number" min="0" className="form-control form-control-sm" id="txtUpdate_SelectSeat" name="seat" placeholder="0"
                      value={newFare.seat} onChange={handleNewFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                  </div>
                  {[
                    { id: "txtUpdate_BasicFare", name: "basic", label: "Basic Fare" },
                    { id: "txtUpdate_YQFare", name: "yq", label: "YQ Fare" },
                    { id: "txtUpdate_YRFare", name: "yr", label: "YR Fare" },
                    { id: "txtUpdate_OTFare", name: "ot", label: "OT Fare" },
                  ].map(({ id, name, label }) => (
                    <div className="col-md-2" key={id}>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                      <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                        value={newFare[name]} onChange={handleNewFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                    </div>
                  ))}
                  <div className="col-md-3">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Infant Fare</label>
                    <input type="number" min="0" className="form-control form-control-sm" id="txtUpdate_InfantFareNew" name="infant" placeholder="0"
                      value={newFare.infant} onChange={handleNewFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                  </div>
                  <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Markup</label>
                        <input type="number" min="0" className="form-control form-control-sm" id="txtUpdate_NewMarkup1" name="markup1" placeholder="0"
                         value={newFare.markup1} onChange={handleNewFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                  <div className="col-md-3">
                    <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Grand Total</label>
                    <input type="text" className="form-control form-control-sm" id="txtUpdate_GrandTotalFare" readOnly
                      value={newGrandTotal} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "#f8f9fe", fontSize: "13px" }} />
                  </div>
                  {/* {[
                    { id: "txtUpdate_NewMarkup1", name: "markup1", label: "Markup 1" },
                    { id: "txtUpdate_NewMarkup2", name: "markup2", label: "Markup 2" },
                  ].map(({ id, name, label }) => (
                    <div className="col-md-3" key={id}>
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                      <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                        value={newFare[name]} onChange={handleNewFareChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                    </div>
                  ))} */}
                  <div className="col-md-3 d-flex align-items-end">
                    <button type="button" onClick={handleAddNewFare} disabled={!(newGrandTotal > 0) || seatSaving} className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                      style={{ background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(14,106,150,0.25)", fontSize: "13px", height: "34px" }}>
                      <i className="bi bi-floppy-fill"></i> Add New Fare
                    </button>
                  </div>
                </div>
              </div>}

            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Inventory Modal ── */}
      <div className="modal fade" id="UpdateInventory" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))" }}></div>
            <div className="modal-header border-0">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className={`bi ${ACTION_ICONS.edit} text-white`} data-action="edit" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: "15px" }}>Edit Inventory</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#fff" }}>Update schedule, terminals and PNR details</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={handleUpdateInventory}>
              <div className="modal-body px-4 pb-4">
                {inventoryInfo}

                <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-pencil-square" style={{ color: "var(--btn-bg)", fontSize: "13px" }}></i>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--btn-bg)", textTransform: "uppercase", letterSpacing: "0.7px" }}>Inventory Details</span>
                  </div>
                  <div className="row g-2">
                    {[
                      { name: "departure_time", label: "Departure Time", placeholder: "HH:MM", required: true },
                      { name: "arrival_time", label: "Arrival Time", placeholder: "HH:MM", required: true },
                      { name: "departure_terminal", label: "Dep. Terminal", placeholder: "e.g. T3" },
                      { name: "arrival_terminal", label: "Arr. Terminal", placeholder: "e.g. T1" },
                      { name: "pnr", label: "PNR", placeholder: "PNR", required: false },
                      { name: "fare_code", label: "Fare Code", placeholder: "Fare code" },
                      { name: "hr", label: "Hide Inventory Before (Hr)", placeholder: "Hours" },
                    ].map(({ name, label, placeholder, required }) => (
                      <div className="col-md-4" key={name}>
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}{required && <span className="text-danger"> *</span>}</label>
                        <input type="text" className="form-control form-control-sm" name={name} placeholder={placeholder}
                          value={inventoryForm[name]} onChange={handleInventoryFormChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button type="button" className="btn btn-sm px-3" data-bs-dismiss="modal"
                    style={{ background: "#fff", border: "1px solid #e8edf5", borderRadius: "8px", height: "34px", fontSize: "13px", color: "#444" }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={inventorySaving}
                    className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white px-3"
                    style={{ background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(14,106,150,0.25)", fontSize: "13px", height: "34px" }}>
                    <i className="bi bi-floppy-fill"></i> {inventorySaving ? "Updating..." : "Update Inventory"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* ── Add/Update Seat Modal ── */}
      <div className="modal fade" id="AddUpdateSeat" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow" style={{ borderRadius: "16px", overflow: "hidden" }}>
            <div style={{ height: "4px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))" }}></div>
            <div className="modal-header border-0">
              <div className="d-flex align-items-center gap-3">
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "linear-gradient(135deg, var(--btn-hover-bg), var(--btn-bg))", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="bi bi-person-lines-fill text-white" style={{ fontSize: "15px" }}></i>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: "15px" }}>Seat Details</h6>
                  <p className="mb-0" style={{ fontSize: "11px", color: "#fff" }}>Add or decrease seats for PNR</p>
                </div>
              </div>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body px-4 pb-4">
              {inventoryInfo}

              {/* Tabs */}
              <ul className="nav nav-tabs mb-3" role="tablist" style={{ borderBottom: "1px solid #eef0f8" }}>
                {[
                  { key: "add", label: "Add New Seat", icon: "bi-plus-circle-fill" },
                  { key: "decrease", label: "Decrease Existing Seat", icon: "bi-dash-circle-fill" },
                ].map((t) => (
                  <li className="nav-item" key={t.key} role="presentation">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={seatTab === t.key}
                      className={`nav-link d-flex align-items-center gap-2 ${seatTab === t.key ? "active" : ""}`}
                      onClick={() => changeSeatTab(t.key)}
                      style={{ fontSize: "13px", fontWeight: "600", color: seatTab === t.key ? "var(--btn-bg)" : "#6b7a90" }}
                    >
                      <i className={`bi ${t.icon}`} style={{ fontSize: "12px" }}></i> {t.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Add New Seat tab */}
              {seatTab === "add" && (
                <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                  {/* <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-plus-circle-fill" style={{ color: "#065f46", fontSize: "13px" }}></i>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#065f46", textTransform: "uppercase", letterSpacing: "0.7px" }}>Add New Seat</span>
                  </div> */}
                  <div className="row g-2 align-items-end">
                    {[
                      { id: "txtUpdate_AddSeat_SeatNumber", name: "seats", label: "No of Seat", col: 3 },
                      { id: "txtUpdate_AddSeat_BasicFare", name: "basic", label: "Basic Fare", col: 3 },
                      { id: "txtUpdate_AddSeat_YQFare", name: "yq", label: "YQ Fare", col: 3 },
                      { id: "txtUpdate_AddSeat_YRFare", name: "yr", label: "YR Fare", col: 3 },
                      { id: "txtUpdate_AddSeat_OTFare", name: "ot", label: "OT Fare", col: 3 },
                      { id: "txtUpdate_AddSeat_InfantFare", name: "infant", label: "Infant Fare", col: 3 },
                    ].map(({ id, name, label, col }) => (
                      <div className={`col-md-${col}`} key={id}>
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                        <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                          value={addSeat[name]} onChange={handleAddSeatChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    ))}
                    <div className="col-md-3">
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Markup</label>
                        <input type="number" min="0" className="form-control form-control-sm" id="txtUpdate_AddSeat_Markup1" name="markup1" placeholder="0"
                          value={addSeat.markup1} onChange={handleAddSeatChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Grand Total</label>
                      <input type="text" className="form-control form-control-sm" id="txtUpdate_AddSeat_GrandTotalFare" disabled
                        value={addSeatGrandTotal} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "#f8f9fe", fontSize: "13px" }} />
                    </div>
                    {/* {[
                      { id: "txtUpdate_AddSeat_Markup1", name: "markup1", label: "Markup 1" },
                      { id: "txtUpdate_AddSeat_Markup2", name: "markup2", label: "Markup 2" },
                    ].map(({ id, name, label }) => (
                      <div className="col-md-3" key={id}>
                        <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</label>
                        <input type="number" min="0" className="form-control form-control-sm" id={id} name={name} placeholder="0"
                          value={addSeat[name]} onChange={handleAddSeatChange} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "white", fontSize: "13px" }} />
                      </div>
                    ))} */}
                    <div className="col-md-3 d-flex align-items-end">
                      <button type="button" onClick={handleAddNewSeat} disabled={!(addSeatGrandTotal > 0) || seatSaving}
                        className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                        style={{ background: "linear-gradient(135deg, #065f46, #10b981)", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(6,95,70,0.25)", fontSize: "13px", height: "34px" }}>
                        <i className="bi bi-plus-circle-fill"></i> {seatSaving ? "Adding..." : "Add Seat"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Decrease Existing Seat tab */}
              {seatTab === "decrease" && (
                <div className="p-3 rounded-3" style={{ background: "#f8f9fe", border: "1px solid #eef0f8" }}>
                  {/* <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="bi bi-dash-circle-fill" style={{ color: "#dc2626", fontSize: "13px" }}></i>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.7px" }}>Decrease Existing Seat</span>
                  </div> */}
                  <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Select Fare</label>
                      <select className="form-select form-select-sm" id="txtUpdate_MinusSeat_SelectSeat" value={decreaseSlabId}
                        onChange={(e) => { setDecreaseSlabId(e.target.value); setDecreaseSeats(""); }}
                        style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                        <option value="">Select</option>
                        {(fareInventory?.fare_slabs || []).map((x) => (
                          <option key={x.inventory_pnr_id} value={x.inventory_pnr_id}>₹ {Number(x.total_amount ?? 0).toLocaleString("en-IN")}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Available Seat</label>
                      <input type="text" className="form-control form-control-sm" id="txtUpdate_MinusSeat_SeatLeft" disabled
                        value={decreaseSlab ? decreaseAvailable : ""} style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", background: "#f8f9fe", fontSize: "13px" }} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label mb-1" style={{ fontSize: "11px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Seats to Decrease</label>
                      <select className="form-select form-select-sm" id="txtUpdate_MinusSeat_MinusSelectSeat" value={decreaseSeats}
                        disabled={!decreaseSlab || decreaseAvailable < 1}
                        onChange={(e) => setDecreaseSeats(e.target.value)}
                        style={{ borderRadius: "8px", border: "1.5px solid #dde3ef", fontSize: "13px" }}>
                        <option value="">Select</option>
                        {Array.from({ length: decreaseAvailable }, (_, i) => i + 1).map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3 d-flex align-items-end">
                      <button type="button" onClick={handleDecreaseSeat} disabled={!decreaseSlab || !(Number(decreaseSeats) > 0) || seatSaving}
                        className="btn btn-sm fw-semibold d-flex align-items-center gap-2 text-white w-100 justify-content-center"
                        style={{ background: "linear-gradient(135deg, #991b1b, #dc2626)", borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(220,38,38,0.25)", fontSize: "13px", height: "34px" }}>
                        <i className="bi bi-dash-circle-fill"></i> {seatSaving ? "Decreasing..." : "Decrease Seat"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ListInventory